import { clsx } from 'clsx'
import { type FC } from 'react'
import { useIntl } from 'react-intl'

import { OfferV1OfferTypeEnum } from '@redteclab/api/clients/bully'

import { Notification } from 'base-ui'

import { productAccessorGetPharmaBlockedAlternativeText } from '../../../model/productAccessor/productAccessorGetPharmaBlockedAlternativeText'
import { productAccessorHasZavaOdsPartner } from '../../../model/productAccessor/productAccessorHasZavaOdsPartner'
import { productAccessorIsPrescriptionProduct } from '../../../model/productAccessor/productAccessorIsPrescriptionProduct'
import { productAccessorIsProductNotInCatalog } from '../../../model/productAccessor/productAccessorIsProductNotInCatalog'
import { productAccessorIsProductOutOfSale } from '../../../model/productAccessor/productAccessorIsProductOutOfSale'
import { productAccessorIsProductOutOfStock } from '../../../model/productAccessor/productAccessorIsProductOutOfStock'
import { PRODUCT_TEMPLATES } from '../../../model/productTemplates'
import { ProductDescriptionParsedField } from '../../ProductDescription/ProductDescriptionParsedField'
import { useProductPageContext } from '../../productPageContext'
import { ProductPageOdsDialogCtaButton } from '../../ProductPageOdsDialog/ProductPageOdsDialogCtaButton'
import { ProductPrescriptionTypeSelect } from '../../ProductPrescriptionType'
import { ProductBuyBoxVoucherInfo } from '../ProductBuyBoxVoucherInfo'
import { useHideAddToCart } from '../useHideAddToCart'

import { ProductAddToCartButtonRow } from './ProductAddToCartButtonRow'
import { ProductAddToCartGroupInfo } from './ProductAddToCartGroupInfo'
import { ProductAddToCartGroupPriceInfo } from './ProductAddToCartGroupPriceInfo/ProductAddToCartGroupPriceInfo'

const useShallOdsButtonRender = (): boolean => {
  const { currentVariant, productPageOdsConfig, productTemplate } =
    useProductPageContext()

  return (
    productTemplate === PRODUCT_TEMPLATES.RX &&
    productAccessorHasZavaOdsPartner(currentVariant) &&
    productPageOdsConfig !== null
  )
}

const useRenderPrescriptionSelect = (): boolean => {
  const { currentOffer, currentVariant, productTemplate } =
    useProductPageContext()

  if (
    productAccessorIsProductOutOfStock(currentVariant.stock_status) ||
    productAccessorIsProductOutOfSale(currentVariant.stock_status) ||
    productAccessorIsProductNotInCatalog(currentVariant.stock_status)
  ) {
    return false
  }

  const isRx = productTemplate === PRODUCT_TEMPLATES.RX

  return currentOffer?.offerType
    ? isRx && currentOffer.offerType === OfferV1OfferTypeEnum.Sae
    : isRx
}

const useShouldRenderPharmaBlockedAlternativeText = (): boolean => {
  const { product } = useProductPageContext()

  return (
    !productAccessorIsProductOutOfStock(product.stock_status) ||
    !productAccessorIsProductOutOfSale(product.stock_status) ||
    !productAccessorIsProductNotInCatalog(product.stock_status)
  )
}

export const ProductAddToCartGroup: FC = () => {
  const i18n = useIntl()
  const { product } = useProductPageContext()

  const hideAddToCart = useHideAddToCart()
  const renderPrescriptionSelect = useRenderPrescriptionSelect()
  const shouldShowOdsButton = useShallOdsButtonRender()
  const renderPharmaBlockedAlternativeText =
    useShouldRenderPharmaBlockedAlternativeText()

  if (hideAddToCart) {
    return null
  }

  const pharmaBlockedAlternativeText =
    productAccessorGetPharmaBlockedAlternativeText(product)

  const isPrescription = productAccessorIsPrescriptionProduct(product)

  const shouldShowPharmaBlockedAlternativeText =
    isPrescription &&
    renderPharmaBlockedAlternativeText &&
    pharmaBlockedAlternativeText

  return (
    <div className="flex flex-col gap-2">
      <ProductAddToCartGroupInfo />

      <ProductBuyBoxVoucherInfo />

      <div
        className={clsx(
          'mb-2.5 gap-2.5',
          renderPrescriptionSelect
            ? 'grid grid-cols-3 tablet:grid-cols-2'
            : 'flex',
        )}
      >
        {renderPrescriptionSelect ? (
          <ProductPrescriptionTypeSelect className="col-span-2 tablet:col-auto" />
        ) : null}

        <ProductAddToCartButtonRow
          renderPrescriptionSelect={renderPrescriptionSelect}
        />
      </div>

      {shouldShowPharmaBlockedAlternativeText ? (
        <Notification
          className="my-4 bg-light-primary-low"
          severity="info"
          title={i18n.formatMessage({
            id: 'product.details.pharmaBlockedAlternativeText',
          })}
        >
          <ProductDescriptionParsedField
            field={pharmaBlockedAlternativeText.toString()}
          />
        </Notification>
      ) : null}

      {shouldShowOdsButton ? <ProductPageOdsDialogCtaButton /> : null}
      <ProductAddToCartGroupPriceInfo />
    </div>
  )
}
